import { classNames } from '@/utils/class-names';
import React, { forwardRef, ReactElement, ReactNode } from 'react';
import LoadingSpin from './LoadingSpin';
import { Link } from 'react-router-dom';

import './index.css';

interface CapitalizeTextProps {
  children: ReactNode;
}

const CapitalizeText: React.FC<CapitalizeTextProps> = ({ children }) => {
  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const capitalizeChild = (child: ReactNode): ReactNode => {
    if (typeof child === 'string') {
      return capitalizeFirstLetter(child);
    } else if (React.isValidElement(child)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clonedChild = React.cloneElement(child as ReactElement<any>, {
        children: React.Children.map(child.props.children, capitalizeChild),
      });
      return clonedChild;
    }
    return child;
  };

  return <>{React.Children.map(children, capitalizeChild)}</>;
};

type ButtonProps = {
  children: ReactNode;
  as?: 'button' | 'a';
  onClick?: (e?: React.MouseEvent) => void;
  disabled?: boolean;
  href?: string;
  theme?: 'outline' | 'solid' | 'solid-white' | 'solid-primary-600';
  size?: 'default' | 'lg';
  className?: string;
  type?: 'submit' | 'button';
  loading?: boolean;
  id?: string;
};

type ButtonElementProps = ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorElementProps = ButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;

type ButtonLinkProps = ButtonElementProps | AnchorElementProps;

const getTheme = (theme: 'outline' | 'solid' | 'solid-white' | 'solid-primary-600' = 'solid') => {
  const defaultCss =
    'font-bold disabled:bg-sh-grey-eee disabled:border-sh-grey-eee disabled:cursor-not-allowed disabled:opacity-50';

  switch (theme) {
    case 'outline':
      return `border-2 border-primary-200 bg-white hover:border-[#A1C3EC] selected:border-[#A1C3EC] focus:border-[#A1C3EC] text-black hover:bg-primary-200 sh-button-outline ${defaultCss}`;
    case 'solid':
      return `border-2 border-[#A1C3EC] hover:border-[#6AAEFE] selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-primary-200  text-black sh-button-solid ${defaultCss}`;
    case 'solid-white':
      return `border-2 border-[#A1C3EC] hover:border-[#6AAEFE] selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-white text-primary-600 sh-button-solid ${defaultCss}`;
    case 'solid-primary-600':
      return `border-2 border-[#A1C3EC] hover:border-[#6AAEFE] selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-primary-600 text-white sh-button-solid ${defaultCss}`;
    default:
      return ``;
  }
};

const getSize = (size: 'default' | 'lg') => {
  if (size === 'lg') {
    return `rounded-2xl py-5 px-sh-30`;
  }

  return `rounded-2xl py-3 px-5`;
};

const CustomButton: React.ForwardRefRenderFunction<ButtonLinkProps, ButtonProps> = (
  {
    className = '',
    children,
    onClick,
    type = 'button',
    disabled,
    as,
    // size = 'md',
    theme = 'solid',
    loading,
    href,
    size = 'default',
    ...rest
  },
  ref,
) => {
  const Element = as === 'a' ? Link : 'button';
  const linkProps = as === 'a' ? { to: href } : {};

  return (
    <Element
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      {...linkProps}
      {...rest}
      className={classNames(`sh-button`, getTheme(theme), getSize(size), className)}>
      <div className="sh-button flex items-center gap-2 ">
        {loading && <LoadingSpin />}
        <CapitalizeText>{children}</CapitalizeText>
      </div>
    </Element>
  );
};

export default forwardRef(CustomButton);
